import type { ApprovalResourceType } from '@risksmart-app/shared/approvals/workflows';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Why there is (or is not) a server answer.
 *
 * `legacy` — the `trpc` flag is off; the caller keeps V2 behaviour.
 * `no-workflow` — the flag is ON but no workflow has been chosen, so there is no
 *   domain to ask about. Distinct from `legacy` because the V2 fallback is NOT
 *   available here: `buildApproverFilters` disables its `RoleKey` arm whenever
 *   the flag is on, so on a GLOBAL approval (no ancestor contributors either)
 *   the fallback filters everyone away. Treating this as `legacy` left the
 *   picker enabled and empty — the silent fail-closed this ticket removes.
 * `loading` / `error` — no answer yet; the caller must not present a filtered
 *   list as an answer (see the note on the return value below).
 * `ready` — `eligibleApprovers` is populated and authoritative.
 */
export type EligibleApproverStatus =
  | 'error'
  | 'legacy'
  | 'loading'
  | 'no-workflow'
  | 'ready';

export interface EligibleApproverCandidates {
  userIds: Set<string>;
  /**
   * `null` means groups are UNCONSTRAINED — a global approval has no parent
   * object for the owner/contributor rule to apply to, matching the legacy
   * client, which applied its `groupFilter` only for parented approvals.
   * Distinct from an empty set ("a parent exists and no group qualifies").
   */
  userGroupIds: Set<string> | null;
}

/**
 * Who may be named as an approver on an approval configuration (COI-571).
 *
 * The server answers the whole rule — the `${resourceType}_approval` Manager
 * companion OR owner/contributor of the parent object — so the client does not
 * re-derive either half. The old client-side arm read
 * `auth.organisationuser."RoleKey"`, a retired V2 column frozen at provisioning,
 * and compared it to `'RiskManager'`, whose meaning changed in V3; it therefore
 * matched nobody and the picker failed closed with no error shown.
 *
 * tRPC-only — there is no GraphQL equivalent, so this is a thin `useQuery`
 * wrapper rather than a `createQueryHook` bridge (the factory needs a document).
 *
 * Returns `null` for `eligibleApprovers` when the `trpc` flag is off (V2 keeps
 * its legacy filter, unchanged), while the answer is still loading, on error,
 * or before a workflow has been chosen. Callers treat `null` as "I have no
 * answer — fall back", never as "nobody is eligible": rendering an empty picker
 * from a non-answer is the exact defect this replaces.
 */
export const useEligibleApproverCandidates = ({
  resourceType,
  parentId,
}: {
  resourceType?: ApprovalResourceType;
  parentId?: string;
}) => {
  const trpc = useTRPC();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const enabled = trpcEnabled && !!resourceType;

  const { data, isError, isPending } = useQuery(
    trpc.frontend.approval.eligibleApproverCandidates.queryOptions(
      enabled && resourceType ? { parentId, resourceType } : skipToken
    )
  );

  const eligibleApprovers = useMemo((): EligibleApproverCandidates | null => {
    if (!enabled || isPending || isError || !data) {
      return null;
    }

    return {
      userGroupIds:
        data.UserGroupIds === null ? null : new Set(data.UserGroupIds),
      userIds: new Set(data.UserIds),
    };
  }, [data, enabled, isError, isPending]);

  // A discriminated state, not just `null` (COI-571 review finding 3).
  //
  // `eligibleApprovers === null` alone cannot tell the caller WHY there is no
  // answer, and the three reasons need different UI:
  //   * flag off        -> use the legacy filter (V2 parity)
  //   * no workflow yet -> there is no domain to ask about, and the legacy
  //                        filter is NOT a valid fallback because the flag is
  //                        on. Block the control until a workflow is chosen.
  //   * pending / error -> do NOT render a filtered list as though it were an
  //                        answer. On a GLOBAL approval the fallback arm has no
  //                        ancestor contributors to fall back to and the legacy
  //                        `RoleKey` arm is disabled, so the picker would show
  //                        NOTHING — the silent fail-closed this ticket exists
  //                        to remove, reintroduced at a different layer.
  //
  // Order matters: a skipped query reports `isPending` forever, so the two
  // reasons the query is skipped are resolved BEFORE the react-query state.
  const status: EligibleApproverStatus = !trpcEnabled
    ? 'legacy'
    : !resourceType
      ? 'no-workflow'
      : isPending
        ? 'loading'
        : isError
          ? 'error'
          : 'ready';

  return { eligibleApprovers, loading: enabled && isPending, status };
};
